import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface FocusModeContextValue {
  focused: boolean;
  setFocused: (value: boolean) => void;
  toggle: () => void;
}

const FocusModeContext = createContext<FocusModeContextValue | null>(null);

/**
 * Lets a page request a chrome-less view of the app shell: the sidebar
 * disappears and the outer gutter padding collapses so the page's own
 * content fills the viewport. Consumed by `AppLayout`.
 */
export function FocusModeProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [focused, setFocused] = useState(false);
  const toggle = useCallback(() => setFocused((f) => !f), []);
  const value = useMemo(
    () => ({ focused, setFocused, toggle }),
    [focused, toggle],
  );
  return (
    <FocusModeContext.Provider value={value}>
      {children}
    </FocusModeContext.Provider>
  );
}

export function useFocusMode(): FocusModeContextValue {
  const ctx = useContext(FocusModeContext);
  if (!ctx) {
    throw new Error("useFocusMode must be used within FocusModeProvider");
  }
  return ctx;
}

/**
 * Convenience hook: enable focus mode while the calling component is
 * mounted, restore it on unmount. Pages that always-on want focus mode
 * shouldn't use this — they should set the state explicitly.
 */
export function useReleaseFocusOnUnmount(): void {
  const { setFocused } = useFocusMode();
  useEffect(
    () => () => {
      setFocused(false);
    },
    [setFocused],
  );
}
